import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import Loading from '../components/Loading';
import { doc, getDoc, setDoc, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // First check if a document exists with auth UID
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);

          if (userDoc.exists()) {
            // If document exists with auth UID, use it
            setCurrentUser({
              id: user.uid,
              ...userDoc.data()
            });
          } else {
            // Check for existing document with email
            const usersRef = collection(db, 'users');
            const emailQuery = query(usersRef, where('email', '==', user.email));
            const querySnapshot = await getDocs(emailQuery);

            if (!querySnapshot.empty) {
              // Found existing document - migrate it
              const oldDoc = querySnapshot.docs[0];
              const oldData = oldDoc.data();

              // Create new document with auth UID
              await setDoc(userRef, oldData);
              
              // Delete old document
              await deleteDoc(oldDoc.ref);

              setCurrentUser({
                id: user.uid,
                ...oldData
              });
            } else {
              // No existing document - create new one
              const userData = {
                email: user.email,
                name: user.displayName || '',
                signedInBefore: false,
                createdAt: new Date().toISOString(),
              };
              await setDoc(userRef, userData);
              setCurrentUser({
                id: user.uid,
                ...userData
              });
            }
          }
        } catch (error) {
          console.error("Error in auth state change:", error);
          setCurrentUser(null);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <AuthContext.Provider value={{ currentUser }}>
      {children}
    </AuthContext.Provider>
  );
};